import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
} from "antd";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  ReloadOutlined,
  EditOutlined,
  FileSearchOutlined,
  CalendarOutlined,
  DollarOutlined,
  CopyOutlined,
  EyeTwoTone,
} from "@ant-design/icons";
import { HEADERS_EXPORT_BEN, POST_MAIN_LAWYER } from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import SendToLawsuit from "./modal/SendToLawsuit";
import MotionHoc from "../../../utils/MotionHoc";
import UpdateStatusBlackNumber from "./modal/UpdateBlackNumber";
import LawyersCheckingDocuments from "./modal/LawyersCheckingDocuments";
import ClearAdvancePaymentOnce from "./modal/ClearAdvancePaymentOnce";
import { Link } from "react-router-dom";
import CopyableLawsuit from "./modal/CopyableLawsuit";

const { Option } = Select;

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));

  // States
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState(7);
  const [isModalLawsuit, setIsModalLawsuit] = useState(false);
  const [isModalBackNumber, setIsModalBlackNumber] = useState(false);
  const [isModalCheckLawyersDoc, setIsModalCheckLawyersDoc] = useState(false);
  const [isModalClearAdvancePayment, setIsModalClearAdvancePayment] =
    useState(false);
  const [isModalCopyable, setIsModalCopyable] = useState(false);
  const [dataRecord, setDataRecord] = useState(null);

  // ✅ โหลดข้อมูลหลัก
  const loadMain = useCallback(async () => {
    setLoading(true);
    let isMounted = true;
    const controller = new AbortController();

    try {
      const res = await axios.post(
        POST_MAIN_LAWYER,
        { userId: userId },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );

      if (!isMounted) return;

      if (res.data && res.data.length > 0) {
        const filteredData = res.data.filter(
          (item) =>
            item.currentStatus === 7 ||
            item.currentStatus === 4 ||
            item.currentStatus === 5 ||
            item.currentStatus === 13,
        );

        setDataArr(filteredData);
        console.log("filteredData", filteredData);
      } else {
        setDataArr([]);
        message.info("ไม่มีข้อมูล");
      }
    } catch (error) {
      if (error.name !== "CanceledError" && isMounted) {
        console.error("❌ Error loading data:", error);
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้";
        message.error(errorMsg);
        setDataArr([]);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  // ✅ ฟังก์ชันสำหรับสร้าง Options ของ Select
  const renderOptionStatus = useCallback(() => {
    const statusOptions = [
      { value: 99, icon: "🗂️", label: "ทั้งหมด" },
      { value: 7, icon: "📝", label: "รอสร้างคำฟ้อง" },
      { value: 4, icon: "📅", label: "รอลงวันนัดศาล" },
      { value: 5, icon: "⚖️", label: "รอพิพากษา" },
      { value: 13, icon: "⚖️", label: "ให้ยื่นอุธรณ์" },
    ];
    return statusOptions.map(({ value, icon, label }) => (
      <Option key={value} value={value}>
        <span style={{ marginRight: 8 }}>{icon}</span>
        {label}
      </Option>
    ));
  }, []);

  // ✅ กรองข้อมูลอัตโนมัติด้วย useMemo
  const arrayTable = useMemo(() => {
    if (!dataArr || dataArr.length === 0) return [];

    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    const searchQuery = normalize(searchInputTable);

    return dataArr.filter((item) => {
      // 1️⃣ กรองตามข้อความค้นหา
      const passQuery =
        !searchQuery ||
        [
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery));

      // 2️⃣ กรองตามสถานะ
      let passStatus = true;
      if (processStatus !== 99 && processStatus != null) {
        passStatus = item.currentStatus === Number(processStatus);
      }

      return passQuery && passStatus;
    });
  }, [dataArr, searchInputTable, processStatus]);

  // ✅ ฟังก์ชันล้างฟิลเตอร์
  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    setProcessStatus(99);
    message.info("ล้างตัวกรองสำเร็จ");
  }, []);

  const handleCreateLawsuit = useCallback((record) => {
    console.log("สร้างคำฟ้อง:", record);
    setDataRecord(record);
    setIsModalLawsuit(true);
  }, []);

  const handleSetCourtDate = useCallback((record) => {
    console.log("ลงวันนัดศาล:", record);
    setDataRecord(record);
    setIsModalBlackNumber(true);
  }, []);

  const handleSetClearAdvancePay = useCallback((record) => {
    setDataRecord(record);
    setIsModalClearAdvancePayment(true);
  }, []);

  const handleEdit = useCallback((record) => {
    console.log("แก้ไข:", record);
    setDataRecord(record);
    setIsModalLawsuit(true);
  }, []);

  // ✅ Callback จาก Modal
  const handleModalCallback = useCallback(() => {
    console.log("📥 Callback from modal");
    loadMain();
  }, [loadMain]);

  // ✅ Columns สำหรับตาราง
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      width: 150,
      render: (_, record) => (
        <Link
          onClick={() => {
            setDataRecord(record);
            setIsModalCopyable(true);
          }}
        >
          {record.CONTNO} <EyeTwoTone />
        </Link>
      ),
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "center",
      render: (_, record) => (
        <span>
          {`${record.SNAM || ""}${record.NAME1 || ""} ${
            record.NAME2 || ""
          }`.trim()}
        </span>
      ),
    },
    {
      title: "วันที่ดำเนินการ",
      align: "center",
      render: (text, record) => {
        const statusConfig = {
          7: {
            color: "red",
            label: "ทนายรับงาน",
            field: record.acceptLawyer,
          },
          4: {
            color: "blue",
            label: "ลงวันประทับฟ้อง",
            field: record.dateOfPlaint,
          },
          5: {
            color: "orange",
            label: "วันนัดศาล",
            field: record.considerationDate,
          },
        };

        const config = statusConfig[record.currentStatus];

        if (!config || !config.field) {
          return <Tag color="default">-</Tag>;
        }

        return (
          <div>
            <Tag color={config.color} style={{ textAlign: "center" }}>
              {config.label}
              <br />
              {dayjs(config.field).add(543, "year").format("D MMM YY")}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      width: 140,
      render: (_, record) => {
        const { currentStatus } = record;

        const statusMap = {
          7: { color: "red", label: "รอสร้างคำฟ้อง" },
          4: { color: "blue", label: "รอลงวันนัดศาล" },
          5: { color: "orange", label: "รอพิพากษา" },
          13: { color: "orange", label: "ให้ยื่นอุธรณ์" },
        };

        const status = statusMap[currentStatus] || {
          color: "default",
          label: "ไม่ระบุ",
        };

        return (
          <Tag color={record.loanActive ? status.color : "default"}>
            <div style={{ textAlign: "center" }}>
              {record.loanActive ? (
                <>
                  <div>{status.label}</div>
                  {currentStatus === 5 && record.considerationDate && (
                    <div style={{ fontSize: 11 }}>
                      {dayjs(record.considerationDate)
                        .add(543, "year")
                        .format("D MMM YY")}
                    </div>
                  )}
                </>
              ) : (
                "ปิดสัญญา"
              )}
            </div>
          </Tag>
        );
      },
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => {
        const { currentStatus } = record;

        if (currentStatus === 7 || currentStatus === 13) {
          return (
            <Button
              danger
              icon={<FileSearchOutlined />}
              // disabled={!record.loanActive}
              onClick={() => handleCreateLawsuit(record)}
            >
              สร้างคำฟ้อง
            </Button>
          );
        }

        if (currentStatus === 4 && record.dateOfPlaint) {
          return (
            <Space size="small" direction="vertical">
              <Button
                color="blue"
                variant="outlined"
                icon={<CalendarOutlined />}
                disabled={!record.loanActive}
                onClick={() => handleSetCourtDate(record)}
                size="small"
                block
              >
                ลงวันนัดศาล
              </Button>
              <Button
                color="orange"
                variant="outlined"
                icon={<EditOutlined />}
                disabled={!record.loanActive}
                onClick={() => handleEdit(record)}
                size="small"
                block
              >
                แก้ไขคำฟ้อง
              </Button>
            </Space>
          );
        }

        if (currentStatus === 5) {
          return (
            <Space size="small" direction="vertical">
              <Button
                color="orange"
                variant="outlined"
                icon={<EditOutlined />}
                disabled={!record.loanActive}
                onClick={() => handleSetCourtDate(record)}
              >
                แก้ไขวันนัดศาล
              </Button>
              {record.currentProcess === 0 && (
                <Button
                  color="green"
                  variant="outlined"
                  icon={<DollarOutlined />}
                  onClick={() => handleSetClearAdvancePay(record)}
                >
                  เคลียร์เงินทดรอง
                </Button>
              )}
            </Space>
          );
        }

        return <span>-</span>;
      },
    },
  ];

  return (
    <>
      <Card>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row gutter={[0, 16]}>
            {/* Filters */}
            <Col span={24}>
              <Space size={10} wrap>
                <Select
                  size="large"
                  style={{ minWidth: 180 }}
                  placeholder="เลือกสถานะ"
                  value={processStatus}
                  onChange={setProcessStatus} // ✅ เปลี่ยนแล้วกรองทันที
                  showSearch
                  optionFilterProp="children"
                >
                  {renderOptionStatus()}
                </Select>

                <Input
                  placeholder="ค้นหาเลขสัญญา, ชื่อ, เลขบัตร"
                  value={searchInputTable}
                  onChange={(e) => setSearchInputTable(e.target.value)} // ✅ พิมพ์แล้วกรองทันที
                  style={{ width: 250, borderRadius: 8 }}
                  size="small"
                  allowClear
                  suffix={<span style={{ marginRight: 5 }}>🔍</span>}
                />

                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilter}
                  size="large"
                  style={{
                    borderRadius: 8,
                    backgroundColor: "#fff1f0",
                    borderColor: "#ffa39e",
                    color: "#cf1322",
                  }}
                >
                  ล้างตัวกรอง
                </Button>
              </Space>
            </Col>

            {/* Table */}
            <Col span={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={arrayTable}
                rowKey={(record) => record.CONTNO || record.id}
                scroll={{ x: 1000 }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                footer={() => (
                  <div style={{ textAlign: "left", fontWeight: 500 }}>
                    จำนวนสัญญาทั้งหมด: {arrayTable?.length || 0} รายการ
                  </div>
                )}
              />
            </Col>
          </Row>
        </Spin>
      </Card>

      {/* Modal */}
      {isModalLawsuit && dataRecord && (
        <SendToLawsuit
          open={isModalLawsuit}
          close={() => setIsModalLawsuit(false)}
          dataDefault={dataRecord}
          funcUpdateStatus={handleModalCallback}
        />
      )}
      {isModalBackNumber && dataRecord && (
        <UpdateStatusBlackNumber
          open={isModalBackNumber}
          close={() => setIsModalBlackNumber(false)}
          dataDefault={dataRecord}
          funcUpdateStatus={handleModalCallback}
        />
      )}
      {isModalCheckLawyersDoc && (
        <LawyersCheckingDocuments
          open={isModalCheckLawyersDoc}
          close={() => setIsModalCheckLawyersDoc(false)}
          dataDefault={dataRecord}
          funcUpdateStatus={handleModalCallback}
        />
      )}
      {isModalClearAdvancePayment && (
        <ClearAdvancePaymentOnce
          open={isModalClearAdvancePayment}
          close={() => setIsModalClearAdvancePayment(false)}
          dataDefault={dataRecord}
          funcUpdateStatus={handleModalCallback}
        />
      )}
      {isModalCopyable && (
        <CopyableLawsuit
          open={isModalCopyable}
          close={() => setIsModalCopyable(false)}
          dataDefault={dataRecord}
        />
      )}
    </>
  );
};

const MainCreateLawsuit = MotionHoc(Main);
export default MainCreateLawsuit;
